// Stripe utilities for loading and initializing Stripe.js

import { loadStripe, Stripe, StripeElementsOptions } from "@stripe/stripe-js";
import { BrandclubStoreConfigUtils } from "@brandclub/common-ui";

let stripePromise: Promise<Stripe | null> | null = null;

/**
 * Get or create Stripe instance using dynamic publishable key
 */
export const getStripe = (): Promise<Stripe | null> => {
  if (!stripePromise) {
    const publishableKey = BrandclubStoreConfigUtils.getStripePublishableKey();
    stripePromise = loadStripe(publishableKey);
  }
  return stripePromise;
};

/**
 * Create Stripe Elements options for Payment Element
 */
export const createElementsOptions = (
  clientSecret?: string
): StripeElementsOptions => {
  const options: StripeElementsOptions = {
    appearance: {
      theme: "stripe" as const,
      variables: {
        colorPrimary: "#0570de",
        colorBackground: "#ffffff",
        colorText: "#30313d",
        colorDanger: "#df1b41",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        spacingUnit: "4px",
        borderRadius: "8px",
      },
      rules: {
        ".Input": {
          border: "1px solid #e0e0e0",
          boxShadow: "none",
        },
        ".Input:focus": {
          border: "1px solid #0570de",
          boxShadow: "0 0 0 1px #0570de",
        },
        ".Label": {
          fontSize: "14px",
          fontWeight: "500",
        },
      },
    },
    loader: "auto",
    // Manual payment method creation to avoid "future payments" message
    paymentMethodCreation: "manual" as const,
  };

  // Add clientSecret if provided (for Payment Intent)
  if (clientSecret) {
    options.clientSecret = clientSecret;
  }

  return options;
};

/**
 * Payment Element options
 */
export const paymentElementOptions = {
  layout: {
    type: "accordion" as const,
    defaultCollapsed: false,
    radios: true,
    spacedAccordionItems: true,
  },
  fields: {
    billingDetails: {
      address: "never" as const, // We collect billing address separately
    },
  },
  terms: {
    card: "never" as const, // Remove terms message for card payments
    googlePay: "never" as const, // Remove terms message for Google Pay
    applePay: "never" as const, // Remove terms message for Apple Pay
    paypal: "never" as const, // Remove terms message for PayPal
    bancontact: "never" as const, // Remove terms message for other methods
    ideal: "never" as const,
    sepaDebit: "never" as const,
  },
};
